import { useCallback, useEffect, useRef, useState } from 'react';

import type { Notification } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { useAuthContext } from '@/features/auth/AuthContext';
import type { DictKey } from '@/i18n/en';
import { useT } from '@/i18n/LocaleProvider';

const POLL_INTERVAL_MS = 60_000;

function kindLabel(kind: string, t: (key: DictKey) => string): string {
  if (kind === 'proposal.approved') return t('notification_kind_proposal_approved');
  if (kind === 'proposal.rejected') return t('notification_kind_proposal_rejected');
  if (kind === 'page.published') return t('notification_kind_page_published');
  return kind;
}

export function NotificationsBell() {
  const t = useT();
  const auth = useAuthContext();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadNotifications = useCallback(async () => {
    if (!auth.token) {
      return;
    }

    try {
      const resp = await wikiApiClient.getNotifications(auth.token);
      setNotifications(resp.notifications);
      setUnreadCount(resp.unread_count);
      setError(null);
    } catch {
      setError(t('notifications_error_load'));
    }
  }, [auth.token, t]);

  // Initial load and periodic refresh.
  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    loadNotifications();

    const interval = setInterval(loadNotifications, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [auth.isAuthenticated, loadNotifications]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) {
      return;
    }

    function handleOutsideClick(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  const handleToggle = useCallback(async () => {
    const nextOpen = !open;
    setOpen(nextOpen);

    if (nextOpen && auth.token) {
      setLoading(true);

      try {
        const resp = await wikiApiClient.getNotifications(auth.token);
        setNotifications(resp.notifications);
        setUnreadCount(resp.unread_count);
        setError(null);
      } catch {
        setError(t('notifications_error_load'));
      } finally {
        setLoading(false);
      }
    }
  }, [open, auth.token, t]);

  const handleMarkRead = useCallback(
    async (id: string) => {
      if (!auth.token) {
        return;
      }

      try {
        await wikiApiClient.markNotificationRead(id, auth.token);
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        /* best-effort */
      }
    },
    [auth.token],
  );

  const handleMarkAllRead = useCallback(async () => {
    if (!auth.token) {
      return;
    }

    try {
      await wikiApiClient.markAllNotificationsRead(auth.token);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      /* best-effort */
    }
  }, [auth.token]);

  if (!auth.isAuthenticated) {
    return null;
  }

  return (
    <div className="notifications-bell" ref={dropdownRef} data-testid="notifications-bell">
      <button
        aria-label={t('notifications_bell_label')}
        aria-expanded={open}
        className="notifications-bell-btn"
        type="button"
        onClick={handleToggle}
      >
        <span className="notifications-bell-icon" aria-hidden="true">
          🔔
        </span>
        {unreadCount > 0 ? (
          <span className="notifications-bell-badge" data-testid="notifications-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="notifications-dropdown"
          role="dialog"
          aria-label={t('notifications_bell_label')}
          data-testid="notifications-dropdown"
        >
          <div className="notifications-dropdown-header">
            <strong>{t('notifications_bell_label')}</strong>
            {unreadCount > 0 ? (
              <button className="notifications-mark-all" type="button" onClick={handleMarkAllRead}>
                {t('notifications_mark_all_read')}
              </button>
            ) : null}
          </div>

          <div className="notifications-list" data-testid="notifications-list">
            {loading ? (
              <div className="notifications-state">{t('notifications_loading')}</div>
            ) : null}
            {!loading && error ? (
              <div className="notifications-state notifications-error">{error}</div>
            ) : null}
            {!loading && !error && notifications.length === 0 ? (
              <div className="notifications-state">{t('notifications_empty')}</div>
            ) : null}
            {!loading && !error
              ? notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`notifications-item${n.read ? ' notifications-item--read' : ''}`}
                    data-testid="notifications-item"
                  >
                    <div className="notifications-item-content">
                      <span className="notifications-item-kind">{kindLabel(n.kind, t)}</span>
                      <span className="notifications-item-summary">{n.summary}</span>
                    </div>
                    {!n.read ? (
                      <button
                        className="notifications-item-read-btn"
                        type="button"
                        aria-label="Mark as read"
                        onClick={() => handleMarkRead(n.id)}
                      >
                        ✓
                      </button>
                    ) : null}
                  </div>
                ))
              : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
